import {
  mdiWeb,
  mdiCart,
  mdiCellphone,
  mdiCog,
  mdiRocket,
  mdiPalette,
  mdiDatabase,
  mdiCloud,
} from '@mdi/js';

export interface Service {
  id: string;
  title: string;
  tagline: string;
  description: string;
  icon: string;
  features: string[];
  technologies: string[];
}

export const services: Service[] = [
  {
    id: 'web-development',
    title: 'Web Development',
    tagline: 'Modern, responsive web applications',
    description: 'From landing pages to complex web applications, we build fast, accessible, and beautiful websites that work on every device. Using modern frameworks and best practices to deliver exceptional user experiences.',
    icon: mdiWeb,
    features: [
      'Single Page Applications (SPA)',
      'Progressive Web Apps (PWA)',
      'Responsive Design',
      'Performance Optimization',
      'SEO Best Practices',
      'Accessibility (WCAG)',
    ],
    technologies: ['React', 'TypeScript', 'Next.js', 'Vite', 'Node.js'],
  },
  {
    id: 'ecommerce',
    title: 'E-Commerce Solutions',
    tagline: 'Sell more, stress less',
    description: 'Complete e-commerce solutions that drive sales. Whether you need a Shopify store, custom cart solution, or integration with existing systems, we deliver storefronts that convert visitors into customers.',
    icon: mdiCart,
    features: [
      'Shopify Development',
      'Custom Cart Solutions',
      'Payment Integration',
      'Inventory Management',
      'Order Fulfillment',
      'Analytics & Reporting',
    ],
    technologies: ['Shopify', 'Stripe', 'WooCommerce', 'Custom Solutions'],
  },
  {
    id: 'mobile-apps',
    title: 'Mobile Apps',
    tagline: 'Native iOS experiences',
    description: 'Beautiful, performant iOS applications built with Swift and SwiftUI. From concept to App Store, we create mobile experiences that users love and keep coming back to.',
    icon: mdiCellphone,
    features: [
      'Native iOS Development',
      'SwiftUI Interfaces',
      'App Store Optimization',
      'Push Notifications',
      'In-App Purchases',
      'Analytics Integration',
    ],
    technologies: ['Swift', 'SwiftUI', 'Xcode', 'TestFlight', 'Firebase'],
  },
  {
    id: 'systems-automation',
    title: 'Systems Automation',
    tagline: 'Work smarter, not harder',
    description: 'Automate repetitive tasks and integrate your systems. We build custom solutions that connect your tools, streamline workflows, and free up your team to focus on what matters.',
    icon: mdiCog,
    features: [
      'Workflow Automation',
      'API Integrations',
      'Data Pipelines',
      'Custom Scripts',
      'Scheduled Tasks',
      'Monitoring & Alerts',
    ],
    technologies: ['Node.js', 'Python', 'Firebase Functions', 'Zapier', 'REST APIs'],
  },
];

export interface ProcessStep {
  number: number;
  title: string;
  description: string;
  icon: string;
}

export const processSteps: ProcessStep[] = [
  {
    number: 1,
    title: 'Discovery',
    description: 'We start by understanding your goals, audience, and requirements. Through collaborative discussions, we define the scope and success metrics for your project.',
    icon: mdiRocket,
  },
  {
    number: 2,
    title: 'Design',
    description: 'We create wireframes and visual designs that align with your brand. You\'ll see exactly how your project will look and feel before we write any code.',
    icon: mdiPalette,
  },
  {
    number: 3,
    title: 'Development',
    description: 'Our team builds your solution using modern technologies and best practices. Regular updates keep you informed of progress throughout the build.',
    icon: mdiDatabase,
  },
  {
    number: 4,
    title: 'Launch',
    description: 'We deploy your project, handle the technical details, and ensure everything runs smoothly. Post-launch support ensures your success continues.',
    icon: mdiCloud,
  },
];

export function getServiceById(id: string): Service | undefined {
  return services.find(service => service.id === id);
}
